import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../api/apiClient";

export interface QuestionNotificationItem {
  programId: string;
  bookingId: string | null;
  programTitle: string;
  programIcon: string;
  pendingCount: number;
  questions: Array<{
    id: string;
    text: string;
    type: string;
    askedOn: string;
  }>;
}

export interface NotificationState {
  loading: boolean;
  totalPendingCount: number; // Number of programs needing attention
  totalQuestionsCount: number; // Total raw question count
  items: QuestionNotificationItem[];
  error: string | null;
  lastNotifiedCount: number;
}

const initialState: NotificationState = {
  loading: false,
  totalPendingCount: 0,
  totalQuestionsCount: 0,
  items: [],
  error: null,
  lastNotifiedCount: 0,
};

let activeNotificationPromise: Promise<any> | null = null;
let lastNotificationFetchTime = 0;

export const fetchUserNotifications = createAsyncThunk(
  "notifications/fetchUserNotifications",
  async (force: boolean | undefined, thunkAPI) => {
    if (activeNotificationPromise) {
      return activeNotificationPromise;
    }
    activeNotificationPromise = (async () => {
      try {
        const response = await apiClient.get("/api/v1/bookings/notifications");
        lastNotificationFetchTime = Date.now();
        return response.data.data;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(
          error.message || "Failed to fetch notifications",
        );
      } finally {
        activeNotificationPromise = null;
      }
    })();
    return activeNotificationPromise;
  },
  {
    condition: (force, { getState }) => {
      if (force === true) return true;
      const state: any = getState();
      if (activeNotificationPromise) {
        return false;
      }
      if (Date.now() - lastNotificationFetchTime < 15000 && state.notifications?.items) {
        return false;
      }
      return true;
    },
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.totalPendingCount = 0;
      state.totalQuestionsCount = 0;
      state.items = [];
      state.lastNotifiedCount = 0;
    },
    setLastNotifiedCount: (state, action) => {
      state.lastNotifiedCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const items = action.payload?.items || [];
        state.items = items;
        // Count number of booked programs that need attention
        state.totalPendingCount = items.length;
        state.totalQuestionsCount = action.payload?.totalPendingCount || 0;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase("user/logoutUser/fulfilled", (state) => {
        state.totalPendingCount = 0;
        state.totalQuestionsCount = 0;
        state.items = [];
        state.lastNotifiedCount = 0;
      });
  },
});

export const { clearNotifications, setLastNotifiedCount } =
  notificationSlice.actions;
export default notificationSlice.reducer;
